import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/admin/Layout';
import { Gift, Plus, Edit, Trash2, Globe } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { getCountryTranslations } from '../../translations/countryTranslations';

interface Reward {
  id: string;
  name: string;
  description: string;
  points_required: number;
  photo_url?: string;
  active: boolean;
  created_at: string;
  message_template?: string;
  language: string;
  translations?: Record<string, any>;
  target_countries?: string[];
}

const Rewards: React.FC = () => {
  const { t } = useTranslation();
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedReward, setSelectedReward] = useState<Reward | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    points_required: 100,
    photo_url: '',
    active: true,
    message_template: '',
    language: 'en',
    translations: {
      fr: { name: '', description: '', message_template: '' },
      es: { name: '', description: '', message_template: '' },
      pt: { name: '', description: '', message_template: '' }
    },
    target_countries: [] as string[]
  });

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'fr', label: 'Français' },
    { value: 'es', label: 'Español' },
    { value: 'pt', label: 'Português' }
  ];

  const countryOptions = getCountryTranslations('en').map(country => ({
    value: country.value,
    label: country.label
  }));

  useEffect(() => {
    fetchRewards();
  }, []);

  const fetchRewards = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('rewards')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRewards(data || []);
    } catch (error) {
      console.error('Error fetching rewards:', error);
      toast.error(t('errorFetchingRewards'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReward = async () => {
    try {
      if (!formData.photo_url) {
        toast.error(t('imageRequired'));
        return;
      }

      const { error } = await supabase
        .from('rewards')
        .insert([{
          name: formData.name,
          description: formData.description,
          points_required: formData.points_required,
          photo_url: formData.photo_url,
          active: formData.active,
          message_template: formData.message_template,
          language: formData.language,
          translations: formData.translations,
          target_countries: formData.target_countries.length > 0 ? formData.target_countries : null
        }]);

      if (error) throw error;

      toast.success(t('rewardCreated'));
      setShowCreateModal(false);
      fetchRewards();
    } catch (error) {
      console.error('Error creating reward:', error);
      toast.error(t('errorCreatingReward'));
    }
  };

  const handleUpdateReward = async () => {
    if (!selectedReward) return;

    try {
      const { error } = await supabase
        .from('rewards')
        .update({
          name: formData.name,
          description: formData.description,
          points_required: formData.points_required,
          photo_url: formData.photo_url,
          active: formData.active,
          message_template: formData.message_template,
          language: formData.language,
          translations: formData.translations,
          target_countries: formData.target_countries.length > 0 ? formData.target_countries : null
        })
        .eq('id', selectedReward.id);

      if (error) throw error;

      toast.success(t('rewardUpdated'));
      setShowEditModal(false);
      fetchRewards();
    } catch (error) {
      console.error('Error updating reward:', error);
      toast.error(t('errorUpdatingReward'));
    }
  };

  const handleDeleteReward = async (id: string) => {
    try {
      const { error } = await supabase
        .from('rewards')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success(t('rewardDeleted'));
      fetchRewards();
    } catch (error) {
      console.error('Error deleting reward:', error);
      toast.error(t('errorDeletingReward'));
    }
  };

  const handleTranslationChange = (lang: string, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: {
          ...prev.translations[lang],
          [field]: value
        }
      }
    }));
  };

  const renderTranslationFields = (lang: string) => (
    <div key={lang} className="mt-4 p-4 bg-gray-50 rounded-lg">
      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
        <Globe className="h-4 w-4 mr-1" />
        {languageOptions.find(opt => opt.value === lang)?.label}
      </h4>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('name')}
          </label>
          <input
            type="text"
            value={formData.translations[lang].name}
            onChange={(e) => handleTranslationChange(lang, 'name', e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('description')}
          </label>
          <textarea
            value={formData.translations[lang].description}
            onChange={(e) => handleTranslationChange(lang, 'description', e.target.value)}
            rows={3}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('messageTemplate')}
          </label>
          <textarea
            value={formData.translations[lang].message_template}
            onChange={(e) => handleTranslationChange(lang, 'message_template', e.target.value)}
            rows={3}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>
      </div>
    </div>
  );

  return (
    <AdminLayout title={t('rewardManagement')}>
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">{t('rewards')}</h3>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-5 w-5 mr-2" />
            {t('createReward')}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : rewards.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {t('noRewards')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('reward')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('points')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('language')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('targetCountries')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('status')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rewards.map((reward) => (
                  <tr key={reward.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {reward.photo_url ? (
                          <img
                            className="h-10 w-10 rounded-full object-cover"
                            src={reward.photo_url}
                            alt={reward.name}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = 'https://placehold.co/100x100/e2e8f0/64748b?text=No+Image';
                            }}
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <Gift className="h-6 w-6 text-blue-600" />
                          </div>
                        )}
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{reward.name}</div>
                          <div className="text-sm text-gray-500">{reward.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{reward.points_required}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Globe className="h-4 w-4 text-gray-500 mr-1" />
                        <span className="text-sm text-gray-900 uppercase">{reward.language}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {reward.target_countries && reward.target_countries.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {reward.target_countries.map(code => (
                              <span key={code} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                {code}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-500">{t('allCountries')}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        reward.active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {reward.active ? t('active') : t('inactive')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedReward(reward);
                          setFormData({
                            name: reward.name,
                            description: reward.description || '',
                            points_required: reward.points_required,
                            photo_url: reward.photo_url || '',
                            active: reward.active,
                            message_template: reward.message_template || '',
                            language: reward.language,
                            translations: reward.translations || {
                              fr: { name: '', description: '', message_template: '' },
                              es: { name: '', description: '', message_template: '' },
                              pt: { name: '', description: '', message_template: '' }
                            },
                            target_countries: reward.target_countries || []
                          });
                          setShowEditModal(true);
                        }}
                        className="text-yellow-600 hover:text-yellow-900 mr-3"
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteReward(reward.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {showCreateModal ? t('createReward') : t('editReward')}
                    </h3>
                    <div className="mt-6 space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t('name')}
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t('description')}
                        </label>
                        <textarea
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          rows={3}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t('pointsRequired')}
                        </label>
                        <input
                          type="number"
                          value={formData.points_required}
                          onChange={(e) => setFormData({ ...formData, points_required: parseInt(e.target.value) })}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t('photoUrl')}
                        </label>
                        <input
                          type="text"
                          value={formData.photo_url}
                          onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t('messageTemplate')}
                        </label>
                        <textarea
                          value={formData.message_template}
                          onChange={(e) => setFormData({ ...formData, message_template: e.target.value })}
                          rows={3}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t('language')}
                        </label>
                        <Select
                          value={languageOptions.find(opt => opt.value === formData.language)}
                          onChange={(selected) => selected && setFormData({ ...formData, language: selected.value })}
                          options={languageOptions}
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t('targetCountries')}
                        </label>
                        <Select
                          isMulti
                          value={countryOptions.filter(opt => formData.target_countries.includes(opt.value))}
                          onChange={(selected) => setFormData({ 
                            ...formData, 
                            target_countries: selected ? selected.map(item => item.value) : [] 
                          })}
                          options={countryOptions}
                          className="mt-1"
                        />
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.active}
                          onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 block text-sm text-gray-900">
                          {t('active')}
                        </label>
                      </div>

                      {/* Translations */}
                      {formData.language === 'en' && (
                        <div className="space-y-4">
                          <h4 className="font-medium text-gray-900">{t('translations')}</h4>
                          {['fr', 'es', 'pt'].map(lang => renderTranslationFields(lang))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={showCreateModal ? handleCreateReward : handleUpdateReward}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {showCreateModal ? t('create') : t('update')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setSelectedReward(null);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {t('cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Rewards;